import { useMemo, useCallback } from "react"
import { Box, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"

import { EmptyData } from "components/EmptyData"
import { PercentIcon } from "components/icons"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { Carousel } from "components/Carousel/Carousel"
import { SearchToggleButton } from "components/domains/search"
import { CarouselItem } from "components/Carousel"
import { Head } from "components/Head"
import { VenueSelector } from "components/VenueSelector"
import { MotionDiv, showContent } from "components/motions"

import { getIcon } from "utils/icon"
import { usePromotion } from "extensions/iconsiam/hooks/promotion"
import { PromotionCard } from "extensions/iconsiam/components/domains/promotions"

export const PromotionsKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { openSearch } = useAppUI()
  const { changeVenueFilter, locationVenueId, venueFilter, promotions } =
    usePromotion()
  const { venues, isMultiVenueProject } = useVenue()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const { styleOverrides } = theme.components?.[`PromotionPage`] || {}
  const { promotionCarouselOption } =
    theme.components[`PromotionPage`]?.defaultProps || {}

  const {
    root,
    carouselContainer,
    carousel,
    emptyDataContainer,
    navigationWrapper,
    venueSelector,
    toolbarContainer,
    actionContainer,
    actionButton,
    searchBarContainer,
  } = styleOverrides
  const slidePerPage = 3
  const carouselType =
    promotions && promotions.length < slidePerPage ? "slide" : "loop"
  const autoplay = promotions && promotions.length > 3
  const focus = promotions.length < 3 ? "center" : promotionCarouselOption.focus
  const direction = theme.direction

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  return (
    <>
      <Head title={t("Promotions")} />
      <Layout>
        <Box sx={root}>
          <MotionDiv
            animate="animate"
            initial="initial"
            variants={showContent}
            sx={carouselContainer}
          >
            {!promotions || promotions.length <= 0 ? (
              <Box sx={emptyDataContainer}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("No Current Promotions")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay,
                  gap: "0",
                  direction: direction,
                  ...promotionCarouselOption,
                  focus,
                }}
                sx={carousel}
              >
                {promotions.map((promotion) => {
                  const sponsoredId = promotion?.properties?.sponsoredId || ""
                  return (
                    <CarouselItem
                      link={`/promotions/${promotion.id}`}
                      key={`${sponsoredId}${promotion.id}`}
                    >
                      <PromotionCard promotion={promotion} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            )}
          </MotionDiv>
          <Box key="app-navigation" sx={navigationWrapper}>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageButton sx={actionButton} />
                {additionalMenu}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
